import {
  Component,
  Show,
  createSignal,
  createEffect,
  onCleanup,
} from "solid-js";

export interface SearchInputProps {
  value?: string;
  placeholder?: string;
  debounceMs?: number;
  disabled?: boolean;
  class?: string;
  onSearch: (value: string) => void;
}

export const SearchInput: Component<SearchInputProps> = (props) => {
  const [localValue, setLocalValue] = createSignal(props.value ?? "");
  let debounceTimer: ReturnType<typeof setTimeout> | undefined;

  // Sync local value when parent changes the prop (e.g., "Clear all")
  createEffect(() => {
    setLocalValue(props.value ?? "");
  });

  onCleanup(() => {
    if (debounceTimer) clearTimeout(debounceTimer);
  });

  const handleInput = (e: InputEvent) => {
    const value = (e.currentTarget as HTMLInputElement).value;
    setLocalValue(value);
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      props.onSearch(value);
    }, props.debounceMs ?? 300);
  };

  const handleClear = () => {
    setLocalValue("");
    if (debounceTimer) clearTimeout(debounceTimer);
    props.onSearch("");
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape" && localValue()) {
      e.preventDefault();
      handleClear();
    }
  };

  return (
    <div class={"relative " + (props.class ?? "")}>
      <input
        type="text"
        class={
          "w-full h-7 pl-9 pr-8 py-0 text-sm bg-surface border border-line rounded placeholder:text-ink-3 focus-ring transition-colors " +
          (props.disabled ? "opacity-50 cursor-not-allowed bg-sunken" : "")
        }
        placeholder={props.placeholder ?? "Search..."}
        value={localValue()}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        disabled={props.disabled}
        aria-label={props.placeholder ?? "Search"}
      />
      <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
        <svg
          class="w-4 h-4 text-ink-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      </div>
      <Show when={localValue()}>
        <button
          type="button"
          class={
            "absolute right-2 top-1/2 -translate-y-1/2 hit-area " +
            "p-0.5 text-ink-3 hover:text-ink rounded-sm transition-colors focus-ring"
          }
          onClick={handleClear}
          aria-label="Clear search"
        >
          <svg
            class="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2.5"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </Show>
    </div>
  );
};
